import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronLeft, ChevronRight, Activity, LogIn, ShieldAlert, KeyRound, Search, Download, X, Filter } from 'lucide-react';
import api from '@/lib/api';

export default function AuditLogPage() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [availableActions, setAvailableActions] = useState([]);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.get('/api/audit-logs/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const params = { page, limit: 30 };
    if (filterType && filterType !== 'all') params.entity_type = filterType;
    if (filterAction && filterAction !== 'all') params.action = filterAction;
    if (filterUser) params.user_name = filterUser;
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;
    if (search) params.search = search;
    return params;
  }, [page, filterType, filterAction, filterUser, filterDateFrom, filterDateTo, search]);

  useEffect(() => {
    setLoading(true);
    api.get('/api/audit-logs', { params: buildParams() })
      .then(r => {
        setLogs(r.data.logs);
        setTotal(r.data.total);
        setPages(r.data.pages);
        if (r.data.available_actions) setAvailableActions(r.data.available_actions);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [buildParams]);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      delete params.page;
      delete params.limit;
      const response = await api.get('/api/audit-logs/export-pdf', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_log_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
    setExporting(false);
  };

  const clearFilters = () => {
    setSearch('');
    setFilterType('');
    setFilterAction('');
    setFilterUser('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setPage(1);
  };

  const hasActiveFilters = search || filterType || filterAction || filterUser || filterDateFrom || filterDateTo;

  const actionColor = {
    sponsor_created: 'bg-blue-100 text-blue-700', right_created: 'bg-emerald-100 text-emerald-700',
    match_created: 'bg-purple-100 text-purple-700', request_created: 'bg-amber-100 text-amber-700',
    request_reviewed: 'bg-amber-100 text-amber-700', right_assigned: 'bg-cyan-100 text-cyan-700',
    request_cancelled: 'bg-red-100 text-red-700', schedule_imported: 'bg-indigo-100 text-indigo-700',
    user_login: 'bg-green-100 text-green-700', user_created: 'bg-blue-100 text-blue-700',
    user_updated: 'bg-teal-100 text-teal-700', user_deleted: 'bg-red-100 text-red-700',
    password_changed: 'bg-yellow-100 text-yellow-700', admin_password_change: 'bg-yellow-100 text-yellow-700',
    cancellation_requested: 'bg-amber-100 text-amber-700', cancellation_rejected: 'bg-rose-100 text-rose-700',
    login_failed: 'bg-red-100 text-red-700', club_created: 'bg-blue-100 text-blue-700',
    club_updated: 'bg-teal-100 text-teal-700', club_deleted: 'bg-red-100 text-red-700',
    sponsor_approved: 'bg-green-100 text-green-700', sponsor_rejected: 'bg-red-100 text-red-700',
  };

  const roleColor = {
    admin: 'bg-purple-100 text-purple-700',
    account_manager: 'bg-blue-100 text-blue-700',
    sponsor: 'bg-amber-100 text-amber-700',
    spo_viewer: 'bg-gray-100 text-gray-600',
  };

  const maxBar = stats ? Math.max(...stats.daily_activity.map(d => d.total), 1) : 1;

  return (
    <div data-testid="audit-log-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('audit_log')}</h1>
          <p className="text-gray-400 mt-1 text-sm">{total} {t('entries')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="export-pdf-btn"
            variant="outline"
            size="sm"
            className="border-gray-300 text-gray-700 hover:bg-gray-50 h-9 text-xs"
            onClick={handleExportPdf}
            disabled={exporting}
          >
            <Download size={14} className="mr-1.5" /> {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-white border-gray-200/80 shadow-none">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center"><Activity size={18} className="text-blue-600" /></div>
                <div><p className="text-2xl font-bold text-gray-900">{stats.today_total}</p><p className="text-[11px] text-gray-400 uppercase tracking-wider">Events Today</p></div>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200/80 shadow-none">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-green-50 flex items-center justify-center"><LogIn size={18} className="text-green-600" /></div>
                <div><p className="text-2xl font-bold text-gray-900">{stats.today_logins}</p><p className="text-[11px] text-gray-400 uppercase tracking-wider">Logins Today</p></div>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200/80 shadow-none">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-red-50 flex items-center justify-center"><ShieldAlert size={18} className="text-red-600" /></div>
                <div><p className="text-2xl font-bold text-gray-900">{stats.today_failed}</p><p className="text-[11px] text-gray-400 uppercase tracking-wider">Failed Logins</p></div>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200/80 shadow-none">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-amber-50 flex items-center justify-center"><KeyRound size={18} className="text-amber-600" /></div>
                <div><p className="text-2xl font-bold text-gray-900">{stats.today_pw_changes}</p><p className="text-[11px] text-gray-400 uppercase tracking-wider">Password Changes</p></div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-white border-gray-200/80 shadow-none">
              <CardContent className="p-5">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-4">Weekly Activity</p>
                <div className="flex items-end justify-between gap-2 h-28">
                  {stats.daily_activity.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] font-medium text-gray-600">{d.total}</span>
                      <div className="w-full rounded-t relative" style={{ height: `${Math.max((d.total / maxBar) * 80, 4)}px` }}>
                        <div className="absolute bottom-0 w-full rounded-t bg-blue-500/20" style={{ height: '100%' }} />
                        {d.logins > 0 && <div className="absolute bottom-0 w-full rounded-t bg-green-500" style={{ height: `${(d.logins / maxBar) * 80}px` }} />}
                        {d.failed > 0 && <div className="absolute bottom-0 w-1/2 right-0 rounded-t bg-red-400" style={{ height: `${(d.failed / maxBar) * 80}px` }} />}
                      </div>
                      <span className="text-[10px] text-gray-400">{d.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-400">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-500" />Logins</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-400" />Failed</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500/20" />Total</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200/80 shadow-none">
              <CardContent className="p-5">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-4">Security Events</p>
                <div className="space-y-2 max-h-[160px] overflow-y-auto">
                  {stats.security_events.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No security events</p>
                  ) : stats.security_events.slice(0, 10).map((e, i) => (
                    <div key={i} className="flex items-start gap-2.5 py-1">
                      <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${e.action === 'login_failed' ? 'bg-red-500' : e.action?.includes('password') ? 'bg-amber-500' : 'bg-blue-500'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-700 truncate">{e.details}</p>
                        <p className="text-[10px] text-gray-400">{e.created_at?.replace('T', ' ').split('.')[0]}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Search & Filters */}
      <Card className="bg-white border-gray-200/80 shadow-none">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="audit-search-input"
                placeholder="Search all fields..."
                className="pl-9 h-9 text-sm border-gray-300"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <Button
              data-testid="toggle-filters-btn"
              variant="outline"
              size="sm"
              className={`border-gray-300 h-9 text-xs ${showFilters ? 'bg-gray-100' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={14} className="mr-1.5" /> Filters {hasActiveFilters && <span className="ml-1 w-2 h-2 rounded-full bg-blue-500" />}
            </Button>
            {hasActiveFilters && (
              <Button data-testid="clear-filters-btn" variant="ghost" size="sm" className="text-xs text-gray-500 h-9" onClick={clearFilters}>
                <X size={14} className="mr-1" /> Clear
              </Button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 pt-2 border-t border-gray-100">
              <Select value={filterType} onValueChange={v => { setFilterType(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-type" className="bg-white border-gray-300 h-9 text-xs"><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="sponsor">Sponsor</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                  <SelectItem value="assignment">Assignment</SelectItem>
                  <SelectItem value="match">Match</SelectItem>
                  <SelectItem value="request">Request</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterAction} onValueChange={v => { setFilterAction(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-action" className="bg-white border-gray-300 h-9 text-xs"><SelectValue placeholder="All Actions" /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 max-h-60">
                  <SelectItem value="all">All Actions</SelectItem>
                  {availableActions.map(a => (
                    <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                data-testid="filter-user"
                placeholder="Filter by user..."
                className="h-9 text-xs border-gray-300"
                value={filterUser}
                onChange={e => { setFilterUser(e.target.value); setPage(1); }}
              />

              <Input
                data-testid="filter-date-from"
                type="date"
                className="h-9 text-xs border-gray-300"
                value={filterDateFrom}
                onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }}
              />

              <Input
                data-testid="filter-date-to"
                type="date"
                className="h-9 text-xs border-gray-300"
                value={filterDateTo}
                onChange={e => { setFilterDateTo(e.target.value); setPage(1); }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-gray-200/80 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead className="text-xs text-gray-400 font-medium">{t('action')}</TableHead>
              <TableHead className="text-xs text-gray-400 font-medium">{t('type')}</TableHead>
              <TableHead className="text-xs text-gray-400 font-medium">{t('user')}</TableHead>
              <TableHead className="text-xs text-gray-400 font-medium">Role</TableHead>
              <TableHead className="text-xs text-gray-400 font-medium">{t('sponsor')}</TableHead>
              <TableHead className="text-xs text-gray-400 font-medium hidden lg:table-cell">{t('details')}</TableHead>
              <TableHead className="text-xs text-gray-400 font-medium">{t('time')}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400 text-sm">{t('loading')}</TableCell></TableRow> :
              logs.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400 text-sm">{t('no_audit')}</TableCell></TableRow> :
              logs.map(l => (
                <TableRow key={l.id} data-testid={`audit-row-${l.id}`} className="border-gray-100 hover:bg-gray-50">
                  <TableCell><Badge className={`text-[11px] pointer-events-none select-none ${actionColor[l.action] || 'bg-gray-100 text-gray-600'}`}>{l.action?.replace(/_/g, ' ')}</Badge></TableCell>
                  <TableCell className="capitalize text-sm text-gray-700">{l.entity_type}</TableCell>
                  <TableCell className="text-sm text-gray-700">{l.user_name || '—'}</TableCell>
                  <TableCell>
                    {l.user_role ? (
                      <Badge className={`text-[10px] pointer-events-none select-none ${roleColor[l.user_role] || 'bg-gray-100 text-gray-600'}`}>
                        {l.user_role.replace(/_/g, ' ')}
                      </Badge>
                    ) : <span className="text-gray-300">—</span>}
                  </TableCell>
                  <TableCell className="text-sm text-gray-700">{l.sponsor_name || '—'}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-gray-500 max-w-xs truncate">{l.details}</TableCell>
                  <TableCell className="text-xs text-gray-500 whitespace-nowrap">{l.created_at?.replace('T', ' ').split('.')[0]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">{t('page_of', { page, pages })}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-gray-300"><ChevronLeft size={16} /></Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="border-gray-300"><ChevronRight size={16} /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
